import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import { Toggle } from "@/components/ui/toggle";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { CloudRain, Radar, Play, Pause } from "lucide-react";
import { cn } from "@/lib/utils";
import { RainStore } from "@/components/Map/Rain/RainStore";
import type { MapStore } from "@/components/Map/stores/MapStore";

interface RainControlsProps {
	mapStore: MapStore<any>;
}

const formatTimestamp = (timestamp: string) => format(new Date(timestamp), "d/M HH:mm");

const RainToggles = observer(({ rainStore }: { rainStore: RainStore }) => (
	<div className="inline-flex flex-col w-[30px] rounded-md bg-white shadow-[0_0_0_2px_#0000001a] overflow-hidden">
		<Tooltip>
			<TooltipTrigger asChild>
				<Toggle
					size="sm"
					pressed={rainStore.showRainGauges}
					onPressedChange={() => rainStore.toggleRainGauges()}
					className={cn(
						"rounded-none bg-white hover:bg-white h-8 px-1.5 min-w-6",
						rainStore.showRainGauges ? "text-[#003BFF]" : "text-gray-300",
					)}
					aria-label="Toggle rain gauges"
				>
					<CloudRain className="h-4 w-4" />
				</Toggle>
			</TooltipTrigger>
			<TooltipContent side="right">Rain gauges</TooltipContent>
		</Tooltip>

		<Tooltip>
			<TooltipTrigger asChild>
				<Toggle
					size="sm"
					pressed={rainStore.showRainRadar}
					onPressedChange={() => rainStore.toggleRainRadar()}
					className={cn(
						"rounded-none border-t border-input bg-white hover:bg-white h-8 px-1.5 min-w-6",
						rainStore.showRainRadar ? "text-[#003BFF]" : "text-gray-300",
					)}
					aria-label="Toggle rain radar"
				>
					<Radar className="h-4 w-4" />
				</Toggle>
			</TooltipTrigger>
			<TooltipContent side="right">Rain radar</TooltipContent>
		</Tooltip>
	</div>
));

const RadarTimelineUI = observer(({ rainStore }: { rainStore: RainStore }) => {
	const { radarTimeline, showRainRadar } = rainStore;
	const { timestamps, selectedIndex, playing } = radarTimeline;
	const visible = showRainRadar && timestamps.length > 0;

	return (
		<AnimatePresence>
			{visible && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: 10 }}
					transition={{ duration: 0.2 }}
					className="absolute bottom-[10px] inset-x-0 mx-auto z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg px-3 py-1.5 max-w-[460px] w-[85%]"
				>
					<div className="flex items-center gap-2">
						<button
							onClick={() => radarTimeline.togglePlay()}
							className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200/60 transition-colors text-gray-600"
						>
							{playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
						</button>
						<span className="text-[10px] text-gray-400 whitespace-nowrap">
							{timestamps.length > 0 ? formatTimestamp(timestamps[0]) : ""}
						</span>
						<input
							type="range"
							min={0}
							max={timestamps.length - 1}
							value={selectedIndex >= 0 ? selectedIndex : timestamps.length - 1}
							onChange={event => {
								radarTimeline.pause();
								radarTimeline.selectIndex(Number(event.target.value));
							}}
							className="flex-1 h-1 accent-[#003BFF] cursor-pointer"
						/>
						<span className="text-[10px] text-gray-400 whitespace-nowrap">
							{timestamps.length > 0 ? formatTimestamp(timestamps[timestamps.length - 1]) : ""}
						</span>
						<span className="text-[11px] font-medium text-gray-700 whitespace-nowrap min-w-[72px] text-right">
							{selectedIndex >= 0 && selectedIndex < timestamps.length
								? formatTimestamp(timestamps[selectedIndex])
								: ""}
						</span>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
});

export const RainControls = observer(({ mapStore }: RainControlsProps) => {
	const { rainStore } = mapStore;

	useEffect(() => {
		if (!mapStore.isLoaded) return;
		rainStore.attach();
		return () => rainStore.detach();
	}, [mapStore.isLoaded, rainStore]);

	return (
		<>
			<div className="absolute left-[10px] bottom-[270px] z-10">
				<RainToggles rainStore={rainStore} />
			</div>
			<RadarTimelineUI rainStore={rainStore} />
		</>
	);
});
